// Voice-to-voice mode (task 7.3, Req 11.4 / 11.6 / 11.7).
//
// The electrician talks to Sparky hands-free: push-to-talk streams mic audio to
// the voice WebSocket, the agent (Nova Sonic) talks back, and a live transcript of
// the back-and-forth is shown on screen (Req 11.4). Everything degrades cleanly to
// typed input when voice can't run — voice not configured (no Identity Pool /
// runtime ARN, so the presign can't be built), a failing presign (not
// authenticated / credential exchange error), no getUserMedia/Web Audio, denied
// mic permission, or the server signalling voiceAvailable:false (Req 11.6). Audio
// is only captured while the push-to-talk control is actively held, and stopping
// is always one obvious tap away (Req 11.7).
//
// Voice creates DRAFTS only. Finalising / sending / pushing still happens on the
// editable review screens behind an explicit on-screen confirm (Req 11.5, gate
// enforced in task 7.4) — so this screen routes the electrician to Quotes/Invoices
// to review anything drafted rather than acting on their behalf.
//
// The pure bits (frame shapes, transcript reducer, PCM/base64 codecs) live in
// ../lib/voice.js and are unit-tested; this module is the browser glue (WebSocket,
// microphone, Web Audio playback) that can't be tested without a DOM.

import { isVoicePresignConfigured, presignVoiceWebSocketUrl } from '../lib/voicePresign.js';
import {
  h, mount, screenEl, card, button, linkButton, buttonRow, muted,
  errorSlot, setError, routeToHash,
} from './ui.js';
import {
  startFrame, audioFrame, textFrame, stopFrame, pingFrame, serializeFrame,
  parseServerEvent, isVoiceUnavailable,
  initTranscript, applyTranscript, completeTurn, encodeCaptureFrame, decodePlaybackFrame,
} from '../lib/voice.js';

const CAPTURE_RATE = 16000; // backend/Nova Sonic input rate
const PLAYBACK_RATE = 24000; // Nova Sonic output rate
const CAPTURE_BUFFER = 4096; // ScriptProcessor frame size
const PING_INTERVAL_MS = 30000;

// ===========================================================================
// Action feed (required — Req 11.16 / design §12)
// ===========================================================================
//
// The action feed is the important on-screen surface (more so than the optional
// transcript — Req 11.17): it shows *what Sparky did* during the conversation so
// the electrician can see and trust it, with links into the review screens.
//
// It is built by OBSERVING the actions that flow through the voice stream. The
// runtime relays the same audit-shaped action events it records server-side
// (audit_writer.py's vocabulary): `clientCreated` / `draftCreated` (each carrying
// the new entity's `entityRef`, plus `toolName` telling quote from invoice on a
// draft), the generic `toolCall` (from which a `recordGuidelineFlags` success is a
// flag recorded), and the protection-`limit` events. `deriveAction` is pure and
// unit-testable; it turns one parsed server event into a feed entry (label + an
// optional review link) or null for a non-action event. The feed itself only ever
// appends — a running record of the session, newest last.

// A short clock timestamp (HH:MM) for a feed entry. Kept relative-free so the
// record reads like a log; falls back to an empty label if Date is unavailable.
function feedClock(date = new Date()) {
  try {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

// Pull the entity ref an action event carries. The runtime embeds it as
// `entityRef` (mirroring audit_writer.entity_ref_from_result); we also accept a
// couple of nested shapes defensively so a forward-compatible payload still reads.
function actionRef(event) {
  if (!event) return '';
  if (typeof event.entityRef === 'string' && event.entityRef.trim()) return event.entityRef.trim();
  const nested = event.client || event.quote || event.invoice || null;
  if (nested && typeof nested === 'object') {
    const ref = nested.ref || nested.name || nested.id;
    if (typeof ref === 'string' && ref.trim()) return ref.trim();
  }
  return '';
}

// Map a parsed server event to a feed entry, or null when it is not an action the
// feed shows. Entry shape: { kind, label, href|null, ts }. `href` uses the same
// routeToHash review links as the review-drafts card, so tapping an entry lands on
// the screen where the electrician reviews/confirms what Sparky drafted.
export function deriveAction(event, now = new Date()) {
  if (!event || typeof event !== 'object') return null;
  const ts = feedClock(now);

  switch (event.type) {
    case 'clientCreated': {
      const ref = actionRef(event);
      return {
        kind: 'client',
        label: ref ? `Created client ${ref}` : 'Created a client',
        href: routeToHash({ section: 'clients' }),
        ts,
      };
    }
    case 'draftCreated': {
      const ref = actionRef(event);
      const isInvoice = event.toolName === 'saveDraftInvoice';
      const noun = isInvoice ? 'invoice' : 'quote';
      const section = isInvoice ? 'invoices' : 'quotes';
      return {
        kind: 'draft',
        label: ref ? `Saved draft ${noun} ${ref}` : `Saved a draft ${noun}`,
        href: routeToHash({ section }),
        ts,
      };
    }
    case 'toolCall': {
      // The only generic tool call the feed surfaces is a recorded guideline flag
      // (clients/drafts get their own dedicated events above). Only a successful
      // call is an action that happened.
      if (event.toolName === 'recordGuidelineFlags' && event.outcome !== 'error') {
        const ref = actionRef(event);
        return {
          kind: 'flag',
          label: ref ? `Recorded a guideline flag for job ${ref}` : 'Recorded a guideline flag',
          href: routeToHash({ section: 'jobs' }),
          ts,
        };
      }
      return null;
    }
    case 'limit': {
      // Protection-limit events are informational — no link needed.
      return {
        kind: 'limit',
        label: event.message || 'Voice session ended (protection limit reached).',
        href: null,
        ts,
      };
    }
    default:
      return null;
  }
}

// Build the action-feed pane: an aria-live region that appends an entry each time
// `observe(event)` sees an action. Returns { el, observe }. Self-contained so the
// screen just mounts `el` and routes every parsed server event through `observe`.
function createActionFeed() {
  const listEl = h('ul', { class: 'voice-action-feed-list' });
  const emptyHint = h('li', {
    class: 'voice-action-feed-empty muted',
    text: 'Anything Sparky does — new clients, draft quotes and invoices, guideline flags — will show here.',
  });
  mount(listEl, emptyHint);
  let count = 0;

  const el = h('div', {
    class: 'voice-action-feed',
    role: 'log',
    'aria-live': 'polite',
    'aria-label': 'What Sparky did',
  }, [
    h('h2', { class: 'voice-action-feed-title', text: 'What Sparky did' }),
    listEl,
  ]);

  const append = (entry) => {
    if (count === 0) listEl.innerHTML = '';
    count += 1;
    const meta = h('span', { class: 'voice-action-meta' }, [
      entry.ts ? h('time', { class: 'voice-action-time', text: entry.ts }) : null,
    ].filter(Boolean));
    const body = entry.href
      ? h('a', { class: 'voice-action-link', href: entry.href, text: entry.label })
      : h('span', { class: 'voice-action-text', text: entry.label });
    const item = h('li', { class: `voice-action voice-action-${entry.kind}` }, [body, meta]);
    listEl.appendChild(item);
    el.scrollTop = el.scrollHeight;
  };

  const observe = (event) => {
    const entry = deriveAction(event);
    if (entry) append(entry);
  };

  return { el, observe };
}

// --- Web Audio availability -------------------------------------------------

function audioContextCtor() {
  return typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
}

// True only when everything the mic path needs exists. Anything missing means we
// stay in typed mode (Req 11.6).
function micSupported() {
  return (
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    !!audioContextCtor()
  );
}

// ===========================================================================
// Voice controller — owns the socket, the microphone and audio playback.
// ===========================================================================

// handlers: { onConnection(state,msg), onTranscript(entries), onCapturing(bool),
//             onVoiceUnavailable(msg), onError(msg) }
function createVoiceController({ handlers }) {
  const on = handlers || {};
  let socket = null;
  let pingTimer = null;
  let transcript = initTranscript();
  // Reuse the same runtime session id across reconnects; each reconnect re-presigns
  // a fresh short-lived URL for it (Req 12.4).
  let sessionId = null;

  // Mic capture graph (created lazily on first push-to-talk).
  let micStream = null;
  let inputCtx = null;
  let sourceNode = null;
  let processor = null;
  let capturing = false;
  let micReady = false;

  // Playback graph.
  let outputCtx = null;
  let nextPlaybackTime = 0;
  // Currently-scheduled playback source nodes, so barge-in can stop them. Nova Sonic
  // streams audio AHEAD of real time (several chunks queued), so stopping generation
  // server-side isn't enough — we must stop the already-scheduled nodes here.
  let scheduledNodes = [];

  const emitTranscript = () => on.onTranscript && on.onTranscript(transcript.entries);

  // --- Socket ---------------------------------------------------------------

  // Open the socket. Resolves TRUE once the socket is open (so callers can connect
  // then immediately start capturing), FALSE if it could not be opened. Never
  // rejects — connection problems are surfaced via the on* handlers and a false
  // return, so callers can `await connect()` and branch cleanly.
  async function connect() {
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
      // Already connected/connecting — treat as success if open, else wait for open.
      if (socket.readyState === WebSocket.OPEN) return true;
    }
    if (!isVoicePresignConfigured()) {
      on.onVoiceUnavailable && on.onVoiceUnavailable('Voice mode is not configured on this deployment.');
      return false;
    }
    on.onConnection && on.onConnection('connecting');
    // Auth now lives entirely in the presign module: it reuses getValidIdToken,
    // exchanges the ID token for temporary Identity Pool creds, and SigV4-presigns a
    // short-lived wss:// URL for the runtime /ws endpoint (Req 12.2/12.4). No more
    // ?token= handshake — the browser can't set Authorization on a WS anyway.
    let url;
    try {
      const presigned = await presignVoiceWebSocketUrl(sessionId ? { sessionId } : {});
      url = presigned.url;
      sessionId = presigned.sessionId; // pin the session across reconnects
    } catch (err) {
      // Not configured / not authenticated / credential exchange failed → fall back
      // to typed input cleanly rather than crashing (Req 11.6).
      // Surface the real cause to the console so failures aren't silent.
      // eslint-disable-next-line no-console
      console.error('[voice] presign/connect failed:', err);
      on.onVoiceUnavailable && on.onVoiceUnavailable('Voice couldn’t start — you can type instead.');
      return false;
    }
    try {
      socket = new WebSocket(url);
    } catch (err) {
      on.onConnection && on.onConnection('error', (err && err.message) || 'Could not open the voice connection.');
      return false;
    }

    // Resolve the promise on the first open/error/close so a caller awaiting
    // connect() knows whether the socket actually came up.
    return await new Promise((resolve) => {
      let settled = false;
      const settle = (ok) => { if (!settled) { settled = true; resolve(ok); } };

      socket.addEventListener('open', () => {
        on.onConnection && on.onConnection('open');
        send(startFrame());
        pingTimer = setInterval(() => send(pingFrame()), PING_INTERVAL_MS);
        settle(true);
      });
      socket.addEventListener('message', (evt) => handleMessage(evt.data));
      socket.addEventListener('error', () => {
        on.onConnection && on.onConnection('error', 'The voice connection had a problem.');
        settle(false);
      });
      socket.addEventListener('close', () => {
        clearInterval(pingTimer);
        pingTimer = null;
        on.onConnection && on.onConnection('closed');
        settle(false);
      });
    });
  }

  function send(frame) {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(serializeFrame(frame));
      return true;
    }
    return false;
  }

  function handleMessage(raw) {
    const event = parseServerEvent(raw);
    // Let the action feed observe every event first (Req 11.16). It only reacts to
    // the action-bearing types (clientCreated / draftCreated / toolCall / limit);
    // everything else passes through untouched to the transcript handling below.
    if (on.onAction) on.onAction(event);
    switch (event.type) {
      case 'transcript':
        transcript = applyTranscript(transcript, event.role, event.text);
        emitTranscript();
        break;
      case 'turnComplete':
        transcript = completeTurn(transcript);
        break;
      case 'interrupted':
        // Barge-in: the user talked over Sparky. Drop any audio already buffered so
        // Sparky goes quiet immediately, and close the open turn like turnComplete.
        stopPlayback();
        transcript = completeTurn(transcript);
        break;
      case 'audio':
        playAudio(event.audio);
        break;
      case 'session':
        if (isVoiceUnavailable(event)) {
          on.onVoiceUnavailable && on.onVoiceUnavailable(event.message || 'Voice is unavailable — you can type instead.');
        }
        break;
      case 'limit':
        // A protection-limit close (Req 13.8): the feed already logged it above;
        // fall back to typed input cleanly (fallback:"typed") rather than hang.
        on.onVoiceUnavailable && on.onVoiceUnavailable(event.message || 'The voice session ended — you can type instead.');
        break;
      case 'error':
        on.onError && on.onError(event.message || 'Voice error.');
        break;
      case 'pong':
      default:
        break;
    }
  }

  // --- Playback (24 kHz PCM from the agent) ---------------------------------

  function ensureOutputCtx() {
    if (outputCtx) return outputCtx;
    const Ctor = audioContextCtor();
    if (!Ctor) return null;
    outputCtx = new Ctor();
    nextPlaybackTime = outputCtx.currentTime;
    return outputCtx;
  }

  function playAudio(base64) {
    const ctx = ensureOutputCtx();
    if (!ctx) return;
    const samples = decodePlaybackFrame(base64);
    if (!samples.length) return;
    // Declare the buffer at the source rate; the context resamples on playback.
    const buffer = ctx.createBuffer(1, samples.length, PLAYBACK_RATE);
    buffer.copyToChannel ? buffer.copyToChannel(samples, 0) : buffer.getChannelData(0).set(samples);
    const node = ctx.createBufferSource();
    node.buffer = buffer;
    node.connect(ctx.destination);
    // Track the node so barge-in can stop it; drop it from the list when it ends on
    // its own, so the list only ever holds still-pending audio.
    scheduledNodes.push(node);
    node.onended = () => {
      const i = scheduledNodes.indexOf(node);
      if (i !== -1) scheduledNodes.splice(i, 1);
    };
    // Schedule chunks back-to-back so speech plays gaplessly.
    const startAt = Math.max(ctx.currentTime, nextPlaybackTime);
    node.start(startAt);
    nextPlaybackTime = startAt + buffer.duration;
  }

  // Barge-in: stop ALL scheduled/playing audio immediately and reset the playback
  // clock so the next turn starts cleanly. Called when the server signals the user
  // interrupted (type:'interrupted'). Stopping a node fires onended, which removes
  // it from scheduledNodes; we also clear the list defensively.
  function stopPlayback() {
    for (const node of scheduledNodes.slice()) {
      try { node.onended = null; node.stop(); } catch { /* already stopped/ended */ }
      try { node.disconnect(); } catch { /* noop */ }
    }
    scheduledNodes = [];
    if (outputCtx) nextPlaybackTime = outputCtx.currentTime;
  }

  // --- Microphone capture ---------------------------------------------------

  // Acquire the mic and build the capture graph. Resolves true on success; on
  // denial/unavailability it signals the typed fallback and resolves false.
  async function initMic() {
    if (micReady) return true;
    if (!micSupported()) {
      on.onVoiceUnavailable && on.onVoiceUnavailable('This device or browser can’t capture audio — you can type instead.');
      return false;
    }
    try {
      micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      on.onVoiceUnavailable && on.onVoiceUnavailable('Microphone access was blocked — you can type instead.');
      return false;
    }
    const Ctor = audioContextCtor();
    inputCtx = new Ctor();
    sourceNode = inputCtx.createMediaStreamSource(micStream);
    processor = inputCtx.createScriptProcessor(CAPTURE_BUFFER, 1, 1);
    processor.onaudioprocess = (e) => {
      // Only stream while push-to-talk is held (Req 11.7).
      if (!capturing) return;
      const input = e.inputBuffer.getChannelData(0);
      const base64 = encodeCaptureFrame(input, inputCtx.sampleRate, CAPTURE_RATE);
      if (base64) send(audioFrame(base64));
    };
    // Gate the mic track off until the user actually holds the button, so no audio
    // is captured while voice mode is merely open (Req 11.7).
    setTrackEnabled(false);
    micReady = true;
    return true;
  }

  function setTrackEnabled(enabled) {
    if (micStream) micStream.getAudioTracks().forEach((t) => { t.enabled = enabled; });
  }

  // Start a session on demand: open the socket (if not already open), then begin
  // capturing. This is what "Start talking" calls, so the AgentCore session only
  // exists while the user is actually talking (Req 11.7). If the socket can't open,
  // beginCapture is skipped and the on* handlers already surfaced the reason.
  async function beginSession() {
    if (capturing) return;
    const connected = await connect();
    if (!connected) return;
    await beginCapture();
  }

  // Begin an utterance: connect the processor and open the mic track.
  async function beginCapture() {
    if (capturing) return;
    const ok = await initMic();
    if (!ok) return;
    if (inputCtx.state === 'suspended') await inputCtx.resume();
    ensureOutputCtx(); // a user gesture is the moment to unlock audio output too
    if (outputCtx && outputCtx.state === 'suspended') outputCtx.resume();
    capturing = true;
    setTrackEnabled(true);
    sourceNode.connect(processor);
    processor.connect(inputCtx.destination);
    on.onCapturing && on.onCapturing(true);
  }

  // End the utterance: stop streaming and tell the agent this turn is done.
  function endCapture() {
    if (!capturing) return;
    capturing = false;
    setTrackEnabled(false);
    try { sourceNode.disconnect(processor); } catch { /* already disconnected */ }
    try { processor.disconnect(); } catch { /* already disconnected */ }
    send(stopFrame());
    on.onCapturing && on.onCapturing(false);
  }

  // Typed fallback: send a message over the same socket (Req 11.6). Echoes it into
  // the transcript so the on-screen record stays complete.
  function sendText(text) {
    const frame = textFrame(text);
    if (!frame.text) return false;
    const ok = send(frame);
    if (ok) {
      transcript = applyTranscript(completeTurn(transcript), 'user', frame.text);
      transcript = completeTurn(transcript);
      emitTranscript();
    }
    return ok;
  }

  // Full teardown: stop capture, release the mic (so the OS indicator clears —
  // Req 11.7), close audio contexts and the socket. Safe to call more than once.
  function stop() {
    endCapture();
    if (micStream) {
      micStream.getTracks().forEach((t) => t.stop());
      micStream = null;
    }
    if (processor) { try { processor.disconnect(); } catch { /* noop */ } processor = null; }
    if (sourceNode) { try { sourceNode.disconnect(); } catch { /* noop */ } sourceNode = null; }
    stopPlayback(); // cut any buffered agent audio before tearing the context down
    if (inputCtx) { inputCtx.close().catch(() => {}); inputCtx = null; }
    if (outputCtx) { outputCtx.close().catch(() => {}); outputCtx = null; }
    micReady = false;
    clearInterval(pingTimer);
    pingTimer = null;
    if (socket) {
      const s = socket;
      socket = null;
      try { s.close(); } catch { /* noop */ }
    }
  }

  return { connect, beginSession, beginCapture, endCapture, sendText, stop, hasMic: micSupported };
}

// ===========================================================================
// Screen
// ===========================================================================

export function voiceScreen() {
  // Voice availability is decided by whether the presign path is configured (Cognito
  // Identity Pool + voice runtime ARN present). Absent ⇒ typed fallback (Req 11.6).
  const voiceConfigured = isVoicePresignConfigured();

  // --- Transcript pane (Req 11.4) -------------------------------------------
  const transcriptEl = h('div', { class: 'voice-transcript', 'aria-live': 'polite', 'aria-label': 'Conversation transcript' });
  const emptyHint = h('p', { class: 'voice-transcript-empty muted', text: 'Your conversation will appear here.' });
  mount(transcriptEl, emptyHint);

  const renderTranscript = (entries) => {
    if (!entries || !entries.length) {
      mount(transcriptEl, emptyHint);
      return;
    }
    const bubbles = entries.map((e) =>
      h('div', { class: `voice-line voice-line-${e.role}` }, [
        h('span', { class: 'voice-line-role', text: e.role === 'user' ? 'You' : 'Sparky' }),
        h('span', { class: 'voice-line-text', text: e.text }),
      ]),
    );
    mount(transcriptEl, h('div', { class: 'voice-lines' }, bubbles));
    transcriptEl.scrollTop = transcriptEl.scrollHeight;
  };

  // --- Status line ----------------------------------------------------------
  const statusDot = h('span', { class: 'voice-status-dot', 'aria-hidden': 'true' });
  const statusText = h('span', { class: 'voice-status-text', text: 'Not connected' });
  const statusEl = h('div', { class: 'voice-status', role: 'status' }, [statusDot, statusText]);
  const setStatus = (state, message) => {
    statusEl.setAttribute('data-state', state);
    const labels = { connecting: 'Connecting…', open: 'Connected', closed: 'Disconnected', error: message || 'Connection problem' };
    statusText.textContent = message || labels[state] || state;
  };

  // --- Controls -------------------------------------------------------------
  // Aura-ring control (design "Option 7"): a rotating conic-gradient halo around a
  // centre disc holding a mic glyph. Idle = calm/still; recording = the halo spins
  // and the centre disc turns red (see .voice-mic in auth.css). The visible label
  // is kept for sighted users and screen readers; setCapturing swaps its text.
  // Mic glyph: the same SVG icon used on the home screen (built as raw SVG markup
  // so it inherits currentColor). Kept aria-hidden — the button already has an
  // accessible label.
  const auraGlyph = h('span', { class: 'voice-aura-glyph', 'aria-hidden': 'true' });
  auraGlyph.innerHTML =
    '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" '
    + 'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">'
    + '<rect x="9" y="2" width="6" height="12" rx="3"></rect>'
    + '<path d="M5 11a7 7 0 0 0 14 0"></path>'
    + '<path d="M12 18v3M8 21h8"></path></svg>';
  const auraCore = h('span', { class: 'voice-aura-core', 'aria-hidden': 'true' }, [auraGlyph]);
  const aura = h('span', { class: 'voice-aura', 'aria-hidden': 'true' }, [auraCore]);
  const micButton = h('button', {
    class: 'voice-mic', type: 'button',
    'aria-label': 'Start talking', title: 'Start talking',
  }, [aura, h('span', { class: 'voice-mic-label', text: 'Start talking' })]);

  const controlsErr = errorSlot();
  const controls = h('div', { class: 'voice-controls' }, [
    micButton,
    controlsErr,
  ]);

  // --- Voice-unavailable notice (Req 11.6) ----------------------------------
  // This page is voice-only: there is no inline typed box. If voice can't run, we
  // show a short notice here and the review links / voice-unavailable card point
  // the electrician at the typed flows on the other screens.
  const typedNotice = muted('');
  typedNotice.style.display = 'none';

  // --- Review-drafts links (Req 11.5) ---------------------------------------
  // Voice only drafts; the electrician reviews/confirms on the editable screens.
  const reviewCard = card([
    muted('Anything Sparky drafts by voice is saved as a draft for you to review, edit and confirm — nothing is sent or pushed automatically.'),
    buttonRow([
      linkButton('Review quotes', routeToHash({ section: 'quotes' })),
      linkButton('Review invoices', routeToHash({ section: 'invoices' })),
      linkButton('View jobs', routeToHash({ section: 'jobs' })),
    ]),
  ], { title: 'Review & confirm' });

  // --- Controller wiring ----------------------------------------------------
  let capturing = false;
  const setCapturing = (isCapturing) => {
    capturing = isCapturing;
    micButton.classList.toggle('is-recording', isCapturing);
    micButton.setAttribute('aria-pressed', isCapturing ? 'true' : 'false');
    const label = isCapturing ? 'Stop talking' : 'Start talking';
    micButton.querySelector('.voice-mic-label').textContent = label;
    micButton.setAttribute('aria-label', label);
    micButton.title = label;
  };

  // Voice unavailable: hide the mic controls and show a short notice. This page has
  // no typed box; the electrician uses the typed flows on the other screens (the
  // review links below / the voice-unavailable card).
  const fallbackToTyped = (message) => {
    controls.style.display = 'none';
    if (message) {
      typedNotice.textContent = message;
      typedNotice.style.display = '';
    }
  };

  const controller = createVoiceController({
    handlers: {
      onConnection: (state, message) => {
        setStatus(state, message);
        if (state === 'error' || state === 'closed') setCapturing(false);
      },
      onTranscript: renderTranscript,
      onCapturing: setCapturing,
      onVoiceUnavailable: (message) => fallbackToTyped(message),
      onError: (message) => setError(controlsErr, message),
    },
  });

  // Tap-to-toggle: press once to start talking, press again to stop. `capturing`
  // (kept in sync by setCapturing) is the source of truth, so the label and the
  // action always agree. A plain click handler covers pointer AND keyboard
  // (Space/Enter activate a button natively), so it is accessible without separate
  // key handling.
  // Start talking → open the session (socket + mic) and capture.
  // Stop talking  → fully stop: this closes the WebSocket, which the runtime sees as
  // a client disconnect and tears down the Nova Sonic bidi stream + releases the
  // session slot (server /ws finally: voice_agent.stop() + CONCURRENCY.release()).
  // So a single button both starts and ENDS the AgentCore session — no separate
  // "End voice" needed.
  const toggleCapture = () => {
    setError(controlsErr, '');
    if (capturing) {
      controller.stop();
      setStatus('closed');
      setCapturing(false);
    } else {
      controller.beginSession();
    }
  };
  micButton.addEventListener('click', (e) => { e.preventDefault(); toggleCapture(); });

  // If audio genuinely can't run, hide the mic and show a notice. This page is
  // voice-only; the review links / voice-unavailable card point at the typed flows.
  if (!controller.hasMic()) {
    fallbackToTyped('This device or browser can’t capture audio — use the review screens to work by typing instead.');
  }

  // --- Body -----------------------------------------------------------------
  let body;
  if (!voiceConfigured) {
    // Voice transport isn't configured: no loss of function — point at typed flows.
    body = card([
      muted('Voice mode isn’t available on this deployment. You can do everything by typing instead.'),
      buttonRow([
        linkButton('New quote', routeToHash({ section: 'quotes', action: 'new' }), { variant: 'primary' }),
        linkButton('New job', routeToHash({ section: 'jobs', action: 'new' })),
      ]),
    ], { title: 'Voice unavailable' });
  } else {
    body = h('div', { class: 'voice-screen' }, [
      controls,
      typedNotice,
      // Live transcript is optional (Req 11.17): kept, collapsed, below the controls.
      h('details', { class: 'voice-transcript-details' }, [
        h('summary', { class: 'voice-transcript-summary', text: 'Show transcript' }),
        transcriptEl,
      ]),
      reviewCard,
    ]);
    // The session is NOT opened on mount: "Start talking" opens the socket + mic
    // together (a user gesture), and "Stop talking" closes the socket, which the
    // runtime treats as a client disconnect and tears the session down. So no
    // AgentCore session (and no Nova Sonic cost) exists until the user taps to talk.
  }

  const screen = screenEl('Voice', body, {
    section: 'voice',
    backHref: routeToHash({ section: 'home' }),
    backLabel: 'Home',
    // Show the connection status inline with the "Voice" heading (no card around
    // it) when voice transport is configured; the unconfigured branch has no live
    // connection to report.
    actions: voiceConfigured ? statusEl : null,
  });

  // The shell calls this when routing away (see shell.js): tear down the socket and
  // release the microphone so nothing keeps capturing off-screen (Req 11.7).
  screen.__cleanup = () => controller.stop();

  // Belt-and-braces: also stop if the tab is hidden or the page unloads.
  const onHide = () => { if (document.visibilityState === 'hidden') controller.endCapture(); };
  const onPageHide = () => controller.stop();
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', onPageHide);
  const baseCleanup = screen.__cleanup;
  screen.__cleanup = () => {
    document.removeEventListener('visibilitychange', onHide);
    window.removeEventListener('pagehide', onPageHide);
    baseCleanup();
  };

  return screen;
}
